import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { CoopenCallbackPage } from './pages/CoopenCallbackPage';
import { FeaturesPage } from './pages/FeaturesPage';
import { PricingPage } from './pages/PricingPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsPage } from './pages/TermsPage';
import { AboutPage } from './pages/AboutPage';
import { ChangelogPage } from './pages/ChangelogPage';
import { DashboardPage } from './pages/DashboardPage';
import { WorkspaceHubPage } from './pages/WorkspaceHubPage';
import { GeneratePage } from './pages/GeneratePage';
import { BrandPage } from './pages/BrandPage';
import { BrandManualPage } from './pages/BrandManualPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { StylesPage } from './pages/StylesPage';
import { ModPage } from './pages/ModPage';
import { CalendarPage } from './pages/CalendarPage';
import { TeamPage } from './pages/TeamPage';
import { ApiKeysPage } from './pages/ApiKeysPage';
import { SubscriptionPage } from './pages/SubscriptionPage';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { WorkspaceProvider } from './providers/WorkspaceProvider';
import { WizardPage as QuickGenWizardPage } from './quickGen/pages/WizardPage';
import { Paywall } from './components/Paywall';

// Code-splitting: las páginas pesadas (editores, admin, campañas, galería, etc.)
// se cargan bajo demanda. Reduce el bundle inicial y acelera el primer paint del
// shell autenticado. Se montan dentro de un <Suspense> con fallback de carga.
const PreviewEditorPage = lazy(() =>
  import('./pages/PreviewEditorPage').then((m) => ({ default: m.PreviewEditorPage })));
const TemplateEditorPreviewPage = lazy(() =>
  import('./pages/TemplateEditorPreviewPage').then((m) => ({ default: m.TemplateEditorPreviewPage })));
const GalleryPage = lazy(() =>
  import('./pages/GalleryPage').then((m) => ({ default: m.GalleryPage })));
const PhotoStylizePage = lazy(() =>
  import('./pages/PhotoStylizePage').then((m) => ({ default: m.PhotoStylizePage })));
const AdminPage = lazy(() =>
  import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })));
const AdminTemplatesPage = lazy(() =>
  import('./pages/AdminTemplatesPage').then((m) => ({ default: m.AdminTemplatesPage })));
const AdminStylesPage = lazy(() =>
  import('./pages/AdminStylesPage').then((m) => ({ default: m.AdminStylesPage })));
const LlmTracePage = lazy(() =>
  import('./pages/LlmTracePage').then((m) => ({ default: m.LlmTracePage })));
const AnalyticsPage = lazy(() =>
  import('./pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })));
const CampaignManagerPage = lazy(() => import('./pages/CampaignManagerPage'));
const CampaignDetailPage = lazy(() => import('./pages/CampaignDetailPage'));
const QuickGenPreviewPage = lazy(() =>
  import('./quickGen/pages/PreviewPage').then((m) => ({ default: m.PreviewPage })));
const AIVisibilityPage = lazy(() =>
  import('./pages/AIVisibilityPage').then((m) => ({ default: m.AIVisibilityPage })));

// Redirige las rutas legacy (/setup, /brand-profile) a la canónica /brand,
// preservando el workspaceId. `replace` para no romper el back del browser.
function BrandRedirect() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  return <Navigate to={`/workspace/${workspaceId}/brand`} replace />;
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return user ? <>{children}</> : <Navigate to="/" />;
}

// Trial freemium: deja entrar si puede usar la app (suscripción activa O le
// quedan créditos de prueba). Sin acceso y sin créditos → Paywall.
function SubscriptionGate({ children }: { children: React.ReactNode }) {
  const { canUse, subscriptionLoading } = useAuth();
  if (canUse === null || subscriptionLoading) {
    return <div>Loading...</div>;
  }
  if (!canUse) {
    return <Paywall />;
  }
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return user ? <Navigate to="/dashboard" /> : <>{children}</>;
}

export function App() {
  return (
    <Routes>
      {/* Landing page — visible siempre. Para users logueados muestra los 2
          CTAs (Legacy / 2.0); para anonimos, mismo CTA + login antes de entrar. */}
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      {/* Password recovery — public, accessible from email link even if logged in */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* CoopenAuth callback — público, recibe redirect de auth.coopenstudio.com */}
      <Route path="/auth/coopen-callback" element={<CoopenCallbackPage />} />
      {/* Marketing & legal pages — open to anyone, logged in or not */}
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/changelog" element={<ChangelogPage />} />
      {/* Authenticated app shell — envuelto en ErrorBoundary (evita pantalla en
          blanco si algún descendiente rompe en render) y Suspense (fallback de
          carga para las páginas lazy del outlet). */}
      <Route
        element={
          <PrivateRoute>
            <SubscriptionGate>
              <WorkspaceProvider>
                <ErrorBoundary>
                  <Suspense fallback={<div>Loading...</div>}>
                    <Layout />
                  </Suspense>
                </ErrorBoundary>
              </WorkspaceProvider>
            </SubscriptionGate>
          </PrivateRoute>
        }
      >
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="mod" element={<ModPage />} />
        <Route path="admin/analytics" element={<AnalyticsPage />} />
        <Route path="admin/llm-trace" element={<LlmTracePage />} />
        <Route path="admin/templates" element={<AdminTemplatesPage />} />
        <Route path="admin/styles" element={<AdminStylesPage />} />
        {/* legacy /quickgen/:id → wizard nuevo /quick-gen (QuickGenPage borrada) */}
        <Route path="quickgen/:workspaceId" element={<Navigate to="/quick-gen" replace />} />
        <Route path="workspace/:workspaceId" element={<WorkspaceHubPage />} />
        {/* Brand unificado — Brand Profile + Brand Setup en una sola página con tabs. */}
        <Route path="workspace/:workspaceId/brand" element={<BrandPage />} />
        {/* Redirects legacy → /brand (compat con links viejos). */}
        <Route path="workspace/:workspaceId/setup" element={<BrandRedirect />} />
        <Route path="workspace/:workspaceId/brand-profile" element={<BrandRedirect />} />
        <Route path="workspace/:workspaceId/generate" element={<GeneratePage />} />
        <Route path="workspace/:workspaceId/posts/:postId/edit" element={<PreviewEditorPage />} />
        <Route path="workspace/:workspaceId/templates" element={<TemplatesPage />} />
        <Route path="workspace/:workspaceId/templates/:templateId" element={<TemplateEditorPreviewPage />} />
        <Route path="workspace/:workspaceId/gallery" element={<GalleryPage />} />
        <Route path="workspace/:workspaceId/ai-visibility" element={<AIVisibilityPage />} />
        <Route path="workspace/:workspaceId/styles" element={<StylesPage />} />
        <Route path="workspace/:workspaceId/photo-stylize" element={<PhotoStylizePage />} />
        <Route path="workspace/:workspaceId/calendar" element={<CalendarPage />} />
        <Route path="workspace/:workspaceId/team" element={<TeamPage />} />
        <Route path="campaigns" element={<CampaignManagerPage />} />
        <Route path="campaigns/:campaignId" element={<CampaignDetailPage />} />
        {/* Suscripción — canónica /subscription. /billing y /usage redirigen acá
            (compat con links/navigate dispersos). */}
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="billing" element={<Navigate to="/subscription" replace />} />
        <Route path="usage" element={<Navigate to="/subscription" replace />} />
        <Route path="settings/api-keys" element={<ApiKeysPage />} />
        {/* Quick Gen: wizard de un paso (URL -> ads). Vive adentro del Layout
            legacy para compartir header/nav. Antes era /v2/*. */}
        <Route path="quick-gen" element={<QuickGenWizardPage />} />
        <Route path="quick-gen/preview/:workspaceId" element={<QuickGenPreviewPage />} />
      </Route>
      {/* Manual de marca — vista standalone PÚBLICA (SIN Layout, SIN auth ni
          suscripción) para compartir como link e imprimir/guardar PDF limpio. El
          workspaceId (hash random de 128 bits) actúa como token de compartir:
          modelo "cualquiera con el link". Lee del endpoint público
          /api/public/brand-manual/:id. */}
      <Route path="/workspace/:workspaceId/brand/manual" element={<BrandManualPage />} />
      {/* Redirect legacy /v2/* -> /quick-gen/* por compat con links viejos. */}
      <Route path="/v2" element={<Navigate to="/quick-gen" replace />} />
      <Route path="/v2/wizard" element={<Navigate to="/quick-gen" replace />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}
